import { useCallback, useState } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import Highlighter from 'react-highlight-words';
import classnames from 'classnames';

import Autocomplete from '@material-ui/lab/Autocomplete';
import InputAdornment from '@material-ui/core/InputAdornment';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import SearchIcon from '@material-ui/icons/Search';

import { TextField } from '@ccm-office/ccm-ui';

import { IMenuSearchItem } from 'components/SearchBar/types';

import { searchStyles, highlighterStyle } from './styles';
import { IOptionType, ISearchFieldProps } from './types';

const startSearchChar: number = 2;

const filterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option: IOptionType) => option.title,
});

function RenderHighliterOption({ searchWords, option }) {
  const highlighterClasses = highlighterStyle();
  return (
    <div>
      <Highlighter
        searchWords={searchWords}
        textToHighlight={option.title}
        autoEscape
        highlightClassName={classnames('bolded-word', highlighterClasses.highlightStyle)}
      />
      {Array.isArray(option.parents) && (
        <div className={highlighterClasses.parentsStyle}>{option.parents.join(' > ')}</div>
      )}
    </div>
  );
}

function RenderNoHighliterOption({ option }) {
  const highlighterClasses = highlighterStyle();
  return (
    <div>
      {option.title}
      {Array.isArray(option.parents) && (
        <div className={highlighterClasses.parentsStyle}>{option.parents.join(' > ')}</div>
      )}
    </div>
  );
}

const handleRenderOption = (option, { inputValue }) => {
  if (inputValue.length > startSearchChar) {
    return <RenderHighliterOption searchWords={[inputValue]} option={option} />;
  }

  return <RenderNoHighliterOption option={option} />;
};

function SearchField(props: ISearchFieldProps) {
  const { t } = useTranslation();
  const classes = searchStyles();
  const history = useHistory();
  const [value, setValue] = useState<IMenuSearchItem>({ path: '', title: '' });
  const [inputValue, setInputValue] = useState<string>('');

  const getOptionSelected = useCallback(
    (option, value) =>
      (value.title === '' && value.path === option.path) || option.title === value.title,
    []
  );
  const getOptionLabel = useCallback((option) => option.title, []);

  const onChange = (event, option: IMenuSearchItem | string | null) => {
    if (option && typeof option === 'object') history.push(option.path);
    const path = typeof option === 'object' ? option?.path : '';
    setValue({ path: path || '', title: '' });
  };

  const handleInputChange = useCallback((event, value) => {
    setInputValue(value);
  }, []);

  return (
    <div className={classes.wrapper}>
      <Autocomplete
        data-testid={'search-menu'}
        open={inputValue.length > startSearchChar}
        onInputChange={handleInputChange}
        className={classes.autocomplete}
        onChange={onChange}
        value={value}
        getOptionLabel={getOptionLabel}
        getOptionSelected={getOptionSelected}
        filterOptions={inputValue.length > startSearchChar ? filterOptions : (option) => option}
        options={props.options}
        noOptionsText={t('label.weCouldNot')}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t('placeholder.search')}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        )}
        renderOption={handleRenderOption}
      />
    </div>
  );
}

export default SearchField;
